'use client'

import { useCallback, useContext, useEffect, useState } from 'react'
import {
  ProductFilterFormContext,
  ProductFilterFormState,
} from '../FilterProductForm/FilterUtils/ProductFilterFormContext'
import { ConfigContext, useTranslation } from '../../context/ConfigContext'
import { SelectionItem } from '../../../domain/selectionItem'
import {
  SORT_BY_PERTINENCE,
  SORT_BY_PRICE_ASC,
} from '../FilterProductForm/FilterProductFormMobile/OrderProducts'
import { isSellingProductOnline } from '../../utils/Product.utils'
import FilterProductForm from '../FilterProductForm/FilterProductForm'
import Layout from '../Layout/Layout'
import Mosaic from '../Mosaic/Mosaic'
import ListSelectionItem from '../ListSelectionItem/ListSelectionItem'
import BreadCrumb from '../BreadCrumb/Breadcrumb'
import { CustomCmsCategory, AggregationsFilters } from '../../../domain/cmsCategory'
import { useIntersectionObserver } from 'usehooks-ts'
import { GtmViewItemListTracker, waitForPageView } from '../../utils/gtmHelpers'
import { useMultipleProductsStockStatusQuery } from '../../../core/hook/useProductStockStatusQuery'

const filterProductsByType = (
  products: SelectionItem[],
  productFilterFormState: ProductFilterFormState,
) => {
  if (!productFilterFormState.types.length) {
    return products
  }

  return products.filter((product) => {
    const idType = product.categoriesOfProduct?.id
    if (!idType) {
      return false
    }
    return productFilterFormState.types.includes(idType)
  })
}
const filterProductsByCollection = (
  products: SelectionItem[],
  productFilterFormState: ProductFilterFormState,
) => {
  if (!productFilterFormState.collections.length) {
    return products
  }

  return products.filter((product) => {
    const idCollection = product.collectionProductExtended?.id
    if (!idCollection) {
      return false
    }
    return productFilterFormState.collections.includes(idCollection)
  })
}

const filterProductsByColors = (
  products: SelectionItem[],
  productFilterFormState: ProductFilterFormState,
) => {
  if (!productFilterFormState.colors.length) {
    return products
  }

  return products.filter((product) => {
    const idColor = product.couleurMetalExtended?.id
    if (!idColor) {
      return false
    }
    return productFilterFormState.colors.includes(idColor)
  })
}

const filterProductsByPriceRange = (
  products: SelectionItem[],
  productFilterFormState: ProductFilterFormState,
) => {
  const { min, max } = productFilterFormState.priceRange
  if (!min || !max) {
    return products
  }

  return products.filter((product) => {
    const price = product.priceRange?.minimum_price.regular_price.value ?? 0
    if (price >= min && price <= max) {
      return true
    }
    return false
  })
}

const filterAvailableOnlineProducts = (
  products: SelectionItem[],
  productFilterFormState: ProductFilterFormState,
  isEcommerce: boolean,
) => {
  const { availableOnline } = productFilterFormState

  if (!availableOnline) {
    return products
  }

  // TODO clean as Product
  return products.filter((product) =>
    isSellingProductOnline({ en_vente: product.enVente } as Product, isEcommerce),
  )
}

const sortProducts = (
  products: SelectionItem[],
  productFilterFormState: ProductFilterFormState,
) => {
  if (productFilterFormState.sortBy === SORT_BY_PERTINENCE) {
    return products
  }

  const sortedProducts = [...products]

  if (productFilterFormState.sortBy === SORT_BY_PRICE_ASC) {
    return sortedProducts.sort((a, b) => {
      const priceA = a.priceRange?.minimum_price.regular_price.value ?? 0
      const priceB = b.priceRange?.minimum_price.regular_price.value ?? 0

      return priceA > priceB ? 1 : -1
    })
  }

  // DESC
  return sortedProducts.sort((a, b) => {
    const priceA = a.priceRange?.minimum_price.regular_price.value ?? 0
    const priceB = b.priceRange?.minimum_price.regular_price.value ?? 0

    return priceA > priceB ? -1 : 1
  })
}

const filterProducts = (
  products: SelectionItem[],
  productFilterFormState: ProductFilterFormState,
  isEcommerce: boolean,
): SelectionItem[] => {
  let results: SelectionItem[] = [...products]
  // types
  results = filterProductsByType(results, productFilterFormState)

  // collections
  results = filterProductsByCollection(results, productFilterFormState)

  // colors
  results = filterProductsByColors(results, productFilterFormState)

  // prices
  results = filterProductsByPriceRange(results, productFilterFormState)

  // available online
  results = filterAvailableOnlineProducts(results, productFilterFormState, isEcommerce)

  // orderby
  results = sortProducts(results, productFilterFormState)
  return results
}

type CollectionPageContentProps = {
  urlPath: string
  urlKey: string
  products: SelectionItem[]
  collectionId?: number | null
  cmsCategory: CustomCmsCategory
  aggregations: AggregationsFilters
}

const SelectionItemWithAnimation = (props: {
  index: number
  product: SelectionItem
  listId?: string
  listName?: string
}) => {
  const { product, index } = props
  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.25,
    freezeOnceVisible: true,
  })

  useEffect(() => {
    if (!isIntersecting) {
      return
    }
    GtmViewItemListTracker.addSelectionItem(product, {
      index: index,
    })
  }, [isIntersecting, product, index])

  return (
    <div className='selection-item-with-animation' key={`selectionItem-${product.id}`} ref={ref}>
      <ListSelectionItem
        item={product}
        isHome={false}
        listId={props.listId || 'categorie'}
        listName={props.listName || "Catégorie d'articles"}
        position={index + 1}
      />
    </div>
  )
}

const Collection = ({
  products: baseProducts,
  urlPath,
  cmsCategory,
  aggregations,
}: CollectionPageContentProps) => {
  const { formState } = useContext(ProductFilterFormContext)
  const { isEcommerce } = useContext(ConfigContext)
  const [products, setProducts] = useState<SelectionItem[]>(baseProducts)
  const filteredProducts = filterProducts(products, formState, isEcommerce())
  const t = useTranslation()
  const productsHotData = useMultipleProductsStockStatusQuery({
    skus: baseProducts.map((p) => p.sku as string),
  })
  const productsHotDataProducts = productsHotData.data
  const collectionContentIntersectionObserver = useIntersectionObserver({
    freezeOnceVisible: true,
    threshold: 1,
  })

  // Create a Cta Array
  const mosaic1: SelectionItem = {
    id: 1,
    title: cmsCategory?.title1,
    link: cmsCategory?.link1,
    videourl: cmsCategory?.videourl1,
    contentimage: cmsCategory?.contentimage1,
    content: cmsCategory?.content1,
    content_color: cmsCategory?.content1_color,
    contentimageposition: cmsCategory?.contentimage1position,
    contentimageside: cmsCategory?.contentimage1side,
    isCta: true,
  }
  const mosaic2: SelectionItem = {
    id: 2,
    title: cmsCategory?.title2,
    link: cmsCategory?.link2,
    videourl: cmsCategory?.videourl2,
    contentimage: cmsCategory?.contentimage2,
    content: cmsCategory?.content2,
    content_color: cmsCategory?.content2_color,
    contentimageposition: cmsCategory?.contentimage2position,
    contentimageside: cmsCategory?.contentimage2side,
    isCta: true,
  }
  const mosaic3: SelectionItem = {
    id: 3,
    title: cmsCategory?.title3,
    link: cmsCategory?.link3,
    videourl: cmsCategory?.videourl3,
    contentimage: cmsCategory?.contentimage3,
    content: cmsCategory?.content3,
    content_color: cmsCategory?.content3_color,
    contentimageposition: cmsCategory?.contentimage3position,
    contentimageside: cmsCategory?.contentimage3side,
    isCta: true,
  }
  const mosaic4: SelectionItem = {
    id: 4,
    title: cmsCategory?.title4,
    link: cmsCategory?.link4,
    videourl: cmsCategory?.videourl4,
    contentimage: cmsCategory?.contentimage4,
    content: cmsCategory?.content4,
    content_color: cmsCategory?.content4_color,
    contentimageposition: cmsCategory?.contentimage4position,
    contentimageside: cmsCategory?.contentimage4side,
    isCta: true,
  }

  const ctaList: SelectionItem[] = [mosaic1, mosaic2, mosaic3, mosaic4]
  let indicator = 1
  const productListWithCta = filteredProducts.reduce((result: SelectionItem[], item, index) => {
    if (index !== 0) {
      result.push(item)
    }

    if (ctaList.length > 0) {
      const cta = ctaList[0]
      const trans = ((cta.contentimageposition ?? 1) - indicator) * 4
      const modulo: any = cta.contentimageside === '0' ? 0 + trans : 2 + trans

      if ((index + 1) % modulo === 0 || modulo === 0) {
        result.push(ctaList.shift() as SelectionItem)
        indicator = indicator + 1
      }
    }

    if (index === 0) {
      result.push(item)
    }

    return result
  }, [])

  const getGtmItemListId = useCallback(() => {
    let id = cmsCategory.canonical_url?.split('/').pop() as string
    const linkFr = cmsCategory.alternates?.find((c) => c?.language_code === 'fr')
    if (linkFr && linkFr.url) {
      const urlFr = new URL(linkFr.url)
      id = urlFr.pathname.split('/').pop() as string
    }
    return id
  }, [])

  useEffect(() => {
    const aborted = { value: false }
    GtmViewItemListTracker.addEventListener('add', async function () {
      const result = await waitForPageView('Collection', aborted)
      if (!result) return
      const queueMaxLen = 4
      if (GtmViewItemListTracker.queue.length >= queueMaxLen) {
        GtmViewItemListTracker.pushEvent({
          item_list_name: "Catégorie d'articles",
          item_list_id: getGtmItemListId(),
        })
      }
    })
    return () => {
      aborted.value = true
      GtmViewItemListTracker.pushEvent({
        item_list_name: "Catégorie d'articles",
        item_list_id: getGtmItemListId(),
      })
      GtmViewItemListTracker.reset()
    }
  }, [])

  useEffect(() => {
    if (collectionContentIntersectionObserver.isIntersecting) {
      GtmViewItemListTracker.pushEvent({
        item_list_name: "Catégorie d'articles",
        item_list_id: getGtmItemListId(),
      })
    }
  }, [collectionContentIntersectionObserver.isIntersecting])

  useEffect(() => {
    if (!productListWithCta.length) {
      return
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, i) => {
        if (entry.isIntersecting) {
          // eslint-disable-next-line no-extra-semi
          ;(entry.target as HTMLElement).style.animationDelay = `${i * 0.5}s`

          entry.target.classList.remove('leave')
          entry.target.classList.add('enter')

          observer.unobserve(entry.target)
        }
      })
    }, {})

    document.querySelectorAll('.selection-item-with-animation').forEach((itemEl) => {
      observer.observe(itemEl)
    })
  }, [productListWithCta?.length])

  useEffect(() => {
    if (productsHotDataProducts) {
      const newProducts = products.map((product) => {
        const productStock = productsHotDataProducts.find(
          (productStock) => productStock?.id == product.id,
        )

        product.enVente = productStock?.en_vente || product.enVente
        product.stockStatus = productStock?.stock_status || product.stockStatus

        return product
      })

      setProducts(newProducts)
    } else {
      setProducts(
        baseProducts.map((product: SelectionItem) => ({ ...product, stockStatus: 'Loading' })),
      )
    }
  }, [productsHotDataProducts])

  const breadcrumb = cmsCategory?.breadcrumbs.map((item) => ({
    id: item.categoryId,
    name: item.categoryName,
    url: item.categoryUrlPath,
  }))

  return (
    <>
      {/* Filters */}
      <FilterProductForm
        aggregations={aggregations}
        products={filteredProducts}
        notFilteredproducts={products}
        urlPath={urlPath}
      />
      <Layout container='padding'>
        <BreadCrumb
          breadcrumbs={breadcrumb || []}
          name={cmsCategory?.cmsBlock?.title || cmsCategory.name}
        />
      </Layout>
      {/* Product list */}
      <Layout container='padding'>
        <div
          ref={collectionContentIntersectionObserver.ref}
          className={`collection-content ${productListWithCta.length > 0 ? 'grid' : ''}`}>
          {productListWithCta.length > 0 ? (
            <>
              {productListWithCta.map((product, i) => {
                if (product.isCta && (product.videourl || product.contentimage)) {
                  return (
                    <Mosaic
                      key={`mosaickey-${product.id}`}
                      className='collection-mosaic'
                      {...product}
                    />
                  )
                }

                if (product.name) {
                  return (
                    <SelectionItemWithAnimation
                      index={i}
                      product={product}
                      listId={getGtmItemListId()}
                      listName="Catégorie d'articles"
                      key={`selection-item-${product.id}`}
                    />
                  )
                }
              })}
            </>
          ) : (
            t("We can't find products matching the selection.", {}, false)
          )}
        </div>
      </Layout>
    </>
  )
}

export default Collection
